import type { ISortDirection } from '@app/lib/sorting'
import type { IApiPatient } from '@app/state/patients/helpers'
import type { IApiVisit, IApiVisitDefinition } from '@app/state/visits/helpers'

import type { IForm } from '../form/form.interface'
import type { Question } from '../instrument'
import type { SubmitResponsesResult } from './visit.rest'
import type { IPatient } from '../patient/patient.interface'
import type { IProgram, IProgramInteraction } from '../program/program.interface'
import type { IUpdateProgramInteractionResponse } from '../program/program.rest'


export type VisitId = string

export enum VisitStatusId {
  Ordered = 'ordered',
  NotStarted = 'not-started',
  InProgress = 'in-progress',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export const NotStartedVisitStatusIds = [
  VisitStatusId.NotStarted,
  VisitStatusId.Ordered,
]

export const OpenVisitStatusIds = [
  VisitStatusId.Ordered,
  VisitStatusId.NotStarted,
  VisitStatusId.InProgress,
]
export const ClosedVisitStatusIds = [
  VisitStatusId.Completed,
  VisitStatusId.Cancelled,
]

export type PdfTemplate = {
  id: string
  label?: string
}

export interface IVisitWithPatient {
  visit: IApiVisit
  patient: IApiPatient
}

export interface IVisit {
  id: VisitId
  startTime: Date
  location: string
  patient?: IPatient
  status?: VisitStatusId
  visitType?: string
  definition?: IVisitDefinition
  state?: IVisitState
}

export enum IWorkFlowStepType {
  Instrument = 'instrument',
  DemographicConfirmation = 'demographic-confirmation',
  Unknown = 'unknown',
}

export interface IWorkflowStep {
  type: IWorkFlowStepType
  instrumentId?: string
  order: number
  workflowLogic: string
}

export interface IVisitDefinition {
  id: string
  name: string
  version: number
  allInstruments?: IDataInstrument[]
  allWorkflow?: IWorkflowStep[]
  visitInstruments?: IDataInstrument[]
  visitWorkflow?: IWorkflowStep[]
  programInteractions?: IProgramInteraction[]
  totalLength?: number
  isSignatureRequired: boolean
  isWitnessSignatureRequired: boolean
  pdfTemplates: PdfTemplate[]
}

export interface IVisitState {
  stage?: string
  instrumentId?: string
  screen?: number
}

export interface IScreenQuestion {
  questionId: string
  displayLogic: string
  sortIndex: number
}

export interface IScreen {
  id: string
  position: number
  header?: string
  content?: string
  flowLogic?: string
  questions?: Array<IScreenQuestion>
}

export enum VisitUseCase {
  Clinic = 'clinic',
  Intake = 'intake',
}

export interface IInstrumentView {
  id: string
  useCase: VisitUseCase
  screens?: IScreen[]
}

export interface IDataInstrument {
  id: string
  type: string
  version: number
  label?: string
  pdfTemplates: PdfTemplate[]
  questions?: Record<string, Question>
  views?: IInstrumentView[]
}

export interface InteractionCommunicationConsent {
  didConsent: boolean
  updatedAt: Date
}

export interface IInteractionType {
  id: string
  name: string
  forms?: IForm[]
}

export interface IUseVisit {
  visit: IVisit
  getVisit: () => void
}

export interface IUseListVisits {
  visits: IVisit[]
  pageCount: number
  totalNumberOfRecords: number
  isLoading: boolean
  getVisits: (
    pageNumber: number,
    recordsPerPage: number,
    location?: string,
    mrn?: string,
    patientName?: string,
    status?: string[],
    visitDate?: string,
    patientId?: string,
    sortDirection?: ISortDirection,
    sortField?: string,
    type?: string[],
  ) => void
}

export type IVisitLocation = string

export interface IUseLocations {
  locations: IVisitLocation[]
  getLocations: () => void
  isLoading: boolean
}

export interface IUseGetVisitDefinition {
  visitDefinition: IApiVisitDefinition
}

export interface IUseGetVisitPrograms {
  getVisitPrograms: () => void
  programs: IProgram[]
}

export interface IUseGetVisitPdf {
  getVisitPdf: (visitId: string, templateId: string) => void
  visitPdfError: string | null
  clearVisitPdfError: () => void
  isVisitPdfLoading: boolean
}

export interface IUseEditEntryReponses {
  editEntryResponses: (
    entryId: string,
    responses: Record<string, string>,
  ) => Promise<SubmitResponsesResult>
}

export interface IUseDerivedValuesForVisit {
  derivedValues: Record<string, unknown>
  getDerivedValues: () => void
}

export interface IUseStartInteraction {
  setInteraction: (
    visitId: string,
    selectedInteractionIds: string[],
  ) => Promise<IUpdateProgramInteractionResponse>
}
